"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useOnboardingStepsContext = exports.OnboardingProvider = void 0;
const use_onboarding_reward_1 = require("@app/component/pages/onboarding/util/use-onboarding-reward");
const use_onboarding_step_toast_1 = require("@app/component/pages/onboarding/util/use-onboarding-step-toast");
const use_onboarding_steps_1 = require("@app/component/pages/onboarding/util/use-onboarding-steps");
const use_publish_onboarding_context_1 = require("@app/component/pages/onboarding/util/use-publish-onboarding-context");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const use_onboarding_1 = require("@app/util/hooks/use-onboarding");
const react_1 = __importDefault(require("react"));
const react_router_dom_1 = require("react-router-dom");
/** the checklist's own route — while it is on screen the state has to be loaded regardless */
const CHECKLIST_PATH = '/onboarding';
const OnboardingStepsContext = react_1.default.createContext(null);
/**
 * The single owner of the onboarding checklist's state, mounted ABOVE the router's `<Switch>`
 * (see `app/routing/Routes.js`) so that it outlives navigation. That is what makes the
 * celebration possible at all: „Vyřešit" sends the manager to the step's own page — which is
 * how the step's Intercom tour gets started — and the work is then finished over there, so
 * whatever is watching for it cannot live on the checklist page.
 *
 * Being the ONLY caller of `useOnboardingSteps` is the other half of the point: one
 * `OnboardingStateQuery`, one websocket subscription, one baseline for the toast. The
 * checklist page reads this context instead of querying a second time.
 *
 * Always rendered, never conditionally — swapping this component out for its children would
 * change the element type above `<Switch>` and remount the entire app on every arm/disarm.
 * `isArmed` is passed down to the hooks instead.
 */
const OnboardingProvider = ({ children, }) => {
    const { pathname } = (0, react_router_dom_1.useLocation)();
    const { isEligible, workspaceId } = (0, use_onboarding_1.useOnboarding)();
    const activeStep = (0, store_1.useRootSelector)((state) => (0, util_1.isValue)(workspaceId) ? state.onboarding[workspaceId]?.activeStep : undefined);
    /**
     * A latch, not `isValue(activeStep)` directly. `useOnboardingStepToast` dispatches
     * `clearedOnboardingStep` the moment it fires, so reading the flag live would disarm the
     * watcher on the first celebration — and the assistant finishing a second step in the same
     * conversation (positions, then employees) would pass unnoticed. Once the manager has
     * started a step, we stay armed for the rest of the session.
     *
     * Holds the workspace id rather than a boolean, which is what disarms it on a switch:
     * a step started in workspace A means nothing in B. A boolean plus a reset effect keyed on
     * `workspaceId` would race on load, where the id and the restored `activeStep` both arrive
     * on the same render and the reset would win.
     */
    const [armedFor, setArmedFor] = react_1.default.useState(undefined);
    react_1.default.useEffect(() => {
        if ((0, util_1.isValue)(activeStep) && (0, util_1.isValue)(workspaceId))
            setArmedFor(workspaceId);
    }, [activeStep, workspaceId]);
    // Nothing on the wire until the manager either opens the checklist or starts a step from
    // it. A manager who never touches it pays for none of this, and it stops for good once the
    // workspace is finished (`isEligible`).
    const isArmed = isEligible &&
        (((0, util_1.isValue)(armedFor) && armedFor === workspaceId) ||
            pathname.includes(CHECKLIST_PATH));
    // The bonus decides whether the checklist even HAS a reward step, so it is read here and
    // handed down — not queried by the card, which would put it below the router.
    const reward = (0, use_onboarding_reward_1.useOnboardingReward)(isArmed);
    const onboardingSteps = (0, use_onboarding_steps_1.useOnboardingSteps)(isArmed, reward.reward);
    // let the manager assistant see the checklist it is here to help finish
    (0, use_publish_onboarding_context_1.usePublishOnboardingContext)(onboardingSteps);
    // celebrate a step becoming done, on whatever page the manager is looking at
    (0, use_onboarding_step_toast_1.useOnboardingStepToast)(onboardingSteps);
    // one loader, not two: the reward decides the step count, so a checklist drawn before it
    // lands would renumber itself under the manager
    const value = {
        ...onboardingSteps,
        isLoading: onboardingSteps.isLoading || reward.isLoading,
        reward,
    };
    return (react_1.default.createElement(OnboardingStepsContext.Provider, { value: value }, children));
};
exports.OnboardingProvider = OnboardingProvider;
/**
 * The checklist state, for the page that draws it. Throws rather than falling back to its own
 * query: a second owner would mean a second baseline for the toast and a duplicate query, and
 * a silent fallback is exactly how that ends up shipped.
 */
const useOnboardingStepsContext = () => {
    const context = react_1.default.useContext(OnboardingStepsContext);
    if (!(0, util_1.isValue)(context)) {
        throw new Error('useOnboardingStepsContext: no OnboardingProvider above this component');
    }
    return context;
};
exports.useOnboardingStepsContext = useOnboardingStepsContext;
exports.default = exports.OnboardingProvider;
